import {
  ChangeEvent,
  ChangeEventHandler,
  FC,
  useCallback,
  useMemo,
  useRef,
  WheelEvent,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import cn from 'classnames';

import { ValueType } from '@/shared/lib/forms/form-wrapper.types';
import { ICollections } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';
import { Input } from '@/shared/uikitV2/Input';
import { isEmpty } from '@/shared/utils/misc';

import { separator } from '../constants';
import { capitalizeValue, EMAIL_REGEX, getRequiredRule } from '../helpers';

type InputComponentProps = {
  name: string;
  question: ValueType<QuestionType, unknown>;
  id: string;
  label: string;
  inputType: string;
  deps?: Array<string>;
  collection?: CollectionName;
  required?: boolean;
  disabled?: boolean;
  isHighlight?: boolean;
};

export const InputComponent: FC<InputComponentProps> = ({
  name,
  question,
  id,
  label,
  inputType,
  deps,
  collection,
  required,
  disabled,
  isHighlight,
}) => {
  const isValueSet = useRef(false);
  const sjDb = useSJDatabase();
  const { control } = useFormContext();

  const emailValidation = (value: string) => {
    if (collection) {
      const isEmailExist = sjDb.collections[collection as keyof ICollections]?.findOne(
        (item: { email?: string }) => {
          return control._formValues.email === item.email;
        },
      );

      if (isEmailExist) {
        return 'Email already exist';
      }
    }

    return !EMAIL_REGEX.test(value) ? 'Email is invalid' : true;
  };

  const compileDepsFunction = useCallback(
    (value: unknown, formValues: Record<string, unknown>) => {
      if (!deps?.length) return true;
      const realNameArr = name.split(separator);

      if (realNameArr.length === 1) {
        return isEmpty(value) && !isEmpty(formValues[deps[0]]) ? 'Required field' : true;
      }

      return isEmpty(value) &&
        // @ts-ignore
        !isEmpty(formValues[realNameArr[0]]?.[realNameArr[1]]?.[deps[0]]?.[realNameArr[3]])
        ? 'Required field'
        : true;
    },
    [deps, name],
  );

  const onInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    fieldFn: ChangeEventHandler<HTMLInputElement>,
  ) => {
    let value = e.target.value;

    if ((question as { capitalized?: boolean }).capitalized) {
      value = capitalizeValue(value);
      e.target.value = value;
    }

    fieldFn(e);
  };

  const commonInputProps = useMemo(() => {
    if (inputType === 'number') {
      return {
        onWheel: (ev: WheelEvent<HTMLInputElement>) => ev.currentTarget.blur(),
      };
    }

    return {};
  }, [inputType]);

  return (
    <Controller
      key={id}
      name={name}
      control={control}
      rules={{
        required: getRequiredRule(question),
        validate: {
          compileDepsFunction: compileDepsFunction,
          ...(inputType === 'email' && question.required && { emailValidation: emailValidation }),
        },
      }}
      render={({ field, fieldState }) => {
        if (!field.value && !isValueSet.current) {
          // field.value = memoizedValue.current[name];
          isValueSet.current = true;
        }

        return (
          <Input
            label={label}
            type={inputType}
            errorText={fieldState.error?.message}
            status={fieldState.error ? 'error' : 'default'}
            required={required}
            disabled={disabled}
            {...field}
            {...commonInputProps}
            className={cn({ highlight: isHighlight && !fieldState.isDirty })}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e, field.onChange)}
          />
        );
      }}
    />
  );
};
